/* eslint-disable complexity */

import { inject, Inject, Injectable } from '@angular/core'
import { CaseDisplay, HearingSectionConflict } from '@ftr/annotations-contracts'
import { HearingSectionSummary } from '@ftr/annotations-contracts/v2'
import {
  AnnotationsApiClient,
  AnnotationsApiClientFactory,
  AnnotationsApiClientFactoryV2,
  AnnotationsApiClientV2,
  CasesService,
  FetchPeopleAnnotationsCommand,
} from '@ftr/api-annotations'
import { RegionalApiClientFactory } from '@ftr/api-regional'
import { AUTH_TOKEN_FETCHER, AuthTokenFetcher } from '@ftr/api-shared'
import {
  AutomaticAnnotationsHearingIdentificationSkipped,
  AutomaticAnnotationsHearingIdentificationSkippedReason,
} from '@ftr/contracts/regional-api'
import { VocabularyTerms } from '@ftr/contracts/type/core'
import { generateUuid, LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { getPlaybackId, RealTimePlaybackKey } from '@ftr/data-realtime-playback'
import {
  assertUnreachable,
  capitalize,
  ConfirmationModalService,
  ModalType,
  tapData,
  tapFailure,
  titleCase,
  ToastComponent,
  ToastService,
} from '@ftr/foundation'
import { HearingSummaryResponse, HearingSummaryResponseSchema } from '@ftr/regional-api-contracts'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { LoggingService } from '@ftr/ui-observability'
import {
  AutomaticAnnotationJobUpdate,
  buildJoinHearingAnnotationsRoomRequest,
  RealTimeLiveHearingsService,
} from '@ftr/ui-user'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { LocalTime } from '@js-joda/core'
import {
  Action,
  Actions,
  createSelector,
  ofActionDispatched,
  Selector,
  State,
  StateContext,
  StateOperator,
} from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { isEmpty, isEqual, keyBy, memoize } from 'lodash-es'
import compact from 'lodash-es/compact'
import { ActiveToast } from 'ngx-toastr'
import {
  combineLatest,
  filter,
  firstValueFrom,
  lastValueFrom,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  takeUntil,
  takeWhile,
  throttleTime,
  timer,
} from 'rxjs'
import { parseStartEndTimes, RealTimeSttMarkerEditSource } from '../../types'
import {
  HearingAnnotationTimeValidation,
  HearingAnnotationTimeValidationReason,
  mapHearingTimeValidationReasonMessage,
  validateHearingSectionTimes,
} from '../../utils'
import { UpdateAutomaticAnnotationJobAction } from './annotations.actions'
import {
  AcceptAutoGeneratedHearingSectionCommand,
  CreateHearingSectionCommand,
  DeleteHearingSectionCommand,
  FetchHearingSectionsCommand,
  GenerateHearingSummaryCommand,
  ListenForHearingUpdatesCommand,
  ResetHearingSectionCommand,
  StopListeningForHearingSectionUpdatesCommand,
  StopOngoingHearingForRecordingCommand,
  UpdateAnnotationBoundariesCommand,
  UpdateAnnotationsLiveLocalTimeCommand,
  UpdateHearingSectionCommand,
  UpdateHearingSectionSummaryVisibilityCommand,
  UpdateSessionAnnotationsCommand,
} from './annotations.commands'
import {
  HearingSectionAcceptedEvent,
  HearingSectionCreatedEvent,
  HearingSectionDeletedEvent,
  HearingSectionUpdatedEvent,
  HearingSectionUpdateFailedEvent,
  HearingSectionUpdateSucceededEvent,
  SessionAnnotationsUpdateFailedEvent,
  SessionAnnotationsUpdateSucceededEvent,
} from './annotations.events'
import { AnnotationsStateModel, HearingSectionModel, RecordingAnnotationsModel } from './annotations.model'
import { HearingAnnotationConflictsModalComponent } from './hearing-annotation-conflicts-modal'

export const AUTO_ANNOS_MIN_LOADING_TOAST_DISPLAY_MS = 3_000
const defaultPlaybackAnnotationsState = (): RecordingAnnotationsModel => ({
  hearingSections: {},
  cases: {},
  annotationBoundaries: [],
  liveLocalTime: undefined,
  latestAutomaticAnnotationJob: undefined,
})

export const getDefaultAnnotationsState: () => AnnotationsStateModel = () => ({
  playbackAnnotations: {},
})

@State({
  name: 'annotations',
  defaults: getDefaultAnnotationsState(),
})
@Injectable()
export class AnnotationsState {
  private readonly casesService = inject(CasesService)

  constructor(
    private readonly annotationsApiClientFactory: AnnotationsApiClientFactory,
    private readonly annotationsApiClientFactoryV2: AnnotationsApiClientFactoryV2,
    @Inject(AUTH_TOKEN_FETCHER) private readonly authTokenFetcher: AuthTokenFetcher,
    private readonly courtSystemRegionCache: CourtSystemRegionCache,
    private readonly realTimeLiveHearingsService: RealTimeLiveHearingsService,
    private readonly actions: Actions,
    private readonly toastService: ToastService,
    private readonly logger: LoggingService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly vocabService: VocabularyTermsService,
    private readonly regionalApiClientFactory: RegionalApiClientFactory,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {}

  @Selector()
  static state(state: AnnotationsStateModel): AnnotationsStateModel {
    return state
  }

  @Selector()
  static allPlaybackInstanceStates(state: AnnotationsStateModel): Record<Uuid, RecordingAnnotationsModel> {
    return state.playbackAnnotations
  }

  static readonly playbackAnnotations = memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((sourceStates: ReturnType<typeof AnnotationsState.allPlaybackInstanceStates>) => RecordingAnnotationsModel) => {
      return createSelector(
        [AnnotationsState.allPlaybackInstanceStates],
        (sourceStates: ReturnType<typeof AnnotationsState.allPlaybackInstanceStates>) =>
          sourceStates[getPlaybackId(playbackKey)] ?? defaultPlaybackAnnotationsState(),
      )
    },
    playbackKey => getPlaybackId(playbackKey),
  )

  @Action(FetchHearingSectionsCommand, { cancelUncompleted: true })
  async fetchHearingSectionsCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: FetchHearingSectionsCommand,
  ): Promise<void> {
    const annotationId = getPlaybackId(action.playbackKey)
    const client = await this.getApiClient(action.courtSystemId)
    const v2client = await this.getV2ApiClient(action.courtSystemId)

    let hearingSectionsRequest
    switch (action.playbackKey.type) {
      case 'audio-segment':
        hearingSectionsRequest = client.hearingSections.getHearingSections({
          query: { audioSegmentId: action.playbackKey.audioSegmentId },
        })
        break
      case 'hearing':
        hearingSectionsRequest = client.hearingSections.getHearingSections({
          query: { hearingId: action.playbackKey.hearingId },
        })
        break
      case 'hearing-section':
        hearingSectionsRequest = v2client.hearingSections.getHearingSection({
          params: { courtSystemId: action.courtSystemId, hearingSectionId: action.playbackKey.hearingSectionId },
        })
        break
      case 'recording':
        hearingSectionsRequest = client.hearingSections.getHearingSections({
          query: { courtSystemId: action.courtSystemId, recordingId: action.playbackKey.recordingId },
        })
        break
      default:
        assertUnreachable(action.playbackKey)
    }

    const summariesRequest = action.playbackKey.recordingId
      ? v2client.hearingSectionSummaries.list({
          params: { courtSystemId: action.courtSystemId },
          query: { recordingId: action.playbackKey.recordingId },
        })
      : undefined

    const existingCases = stateContext.getState().playbackAnnotations[annotationId]?.cases ?? {}
    const casesRequest =
      isEmpty(existingCases) || !!action.withCases
        ? this.casesService.getCaseDisplaysForPlayback(action.playbackKey, action.courtSystemId)
        : existingCases

    const [hearingSectionsResponse, summariesResponse, cases] = await Promise.all([
      hearingSectionsRequest,
      summariesRequest,
      casesRequest,
    ])
    if (hearingSectionsResponse.status !== 200) {
      return
    }

    const summariesByHearingSectionId = new Map<Uuid, HearingSectionSummary>()
    if (summariesResponse?.status === 200) {
      summariesResponse.body.forEach(summary => summariesByHearingSectionId.set(summary.hearingSectionId, summary))
    }

    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)
    const existingHearings = playbackAnnotations.hearingSections
    const newHearingSections = (
      Array.isArray(hearingSectionsResponse.body) ? hearingSectionsResponse.body : [hearingSectionsResponse.body]
    ).reduce<Record<Uuid, HearingSectionModel>>((acc, hearingSection) => {
      const relatedCase = hearingSection.caseId ? cases[hearingSection.caseId] : undefined
      acc[hearingSection.id] = {
        id: hearingSection.id,
        recordingId: hearingSection.recordingId,
        courtSystemId: hearingSection.courtSystemId,
        caseReference: hearingSection.caseReference ?? undefined,
        title: hearingSection.title ?? undefined,
        startTime: hearingSection.startTime,
        endTime: hearingSection.endTime,
        status: existingHearings[hearingSection.id]?.status ?? 'loaded',
        isAutoGenerated: hearingSection.isAutoGenerated,
        hearingId:
          ('hearingId' in hearingSection ? hearingSection.hearingId : hearingSection.legacyHearingGroupId) ?? undefined,
        caseId: relatedCase?.id ?? undefined,
        caseTypeId: relatedCase?.caseTypeId ?? undefined,
        hearingTypeId: hearingSection.hearingTypeId ?? undefined,
        showAsOngoing: isOngoing(playbackAnnotations.liveLocalTime, hearingSection),
        autofocus: false,
        summary: summariesByHearingSectionId.get(hearingSection.id),
      }
      return acc
    }, {})

    stateContext.setState(
      patch({
        playbackAnnotations: patch({
          [annotationId]: patch({ hearingSections: newHearingSections, cases: keyBy(cases ?? [], 'id') }),
        }),
      }),
    )
  }

  @Action(CreateHearingSectionCommand)
  async createHearingSectionCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: CreateHearingSectionCommand,
  ): Promise<void> {
    const terms = await firstValueFrom(this.vocabService.observeTerms(action.courtSystemId))
    const annotationId = getPlaybackId(action.playbackKey)
    const client = await this.getApiClient(action.courtSystemId)
    // Annotations can only be accurate to the second
    const startTime = action.startTime.withNano(0)

    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)
    const triggerResult = await client.courtroomControls.triggerHearingSection({
      body: {
        caseReference: action.caseReference,
        courtSystemId: action.courtSystemId,
        locationId: action.locationId,
        startDateTime: startTime.atDate(action.recordingDate),
        title: action.title,
        courtroomSessionId: action.courtroomSessionId,
      },
    })
    if (triggerResult.status === 201) {
      const { id, courtSystemId, recordingId, caseReference, title, endTime, hearingId, isAutoGenerated } =
        triggerResult.body

      const newHearingSection: HearingSectionModel = {
        id,
        recordingId,
        courtSystemId,
        startTime,
        endTime,
        caseReference: caseReference ?? undefined,
        title: title ?? undefined,
        status: 'saved',
        hearingId: hearingId ?? undefined,
        caseId: undefined, // always undefined from the create result
        caseTypeId: undefined, // always undefined from the create result
        hearingTypeId: undefined, // always undefined from the create result
        showAsOngoing: isOngoing(playbackAnnotations.liveLocalTime, { startTime, endTime }),
        autofocus: caseReference === null && title === null,
        isAutoGenerated,
      }
      stateContext.setState(patchHearingSection(annotationId, id, newHearingSection))

      stateContext.dispatch(
        new HearingSectionCreatedEvent(action.playbackKey, action.courtSystemId, id, action.triggerSource),
      )
    } else {
      this.logger.error({ message: 'Unexpected status code when creating hearing section', response: triggerResult })
      this.toastService.error(`Failed to create ${terms.hearing.singular}.`)
    }
    return
  }

  @Action(UpdateHearingSectionCommand)
  async updateHearingSectionCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: UpdateHearingSectionCommand,
  ): Promise<void> {
    const annotationId = getPlaybackId(action.playbackKey)
    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)
    const originalHearingSection = playbackAnnotations.hearingSections[action.hearingSectionId]
    if (!originalHearingSection) {
      return
    }
    const client = await this.getApiClient(originalHearingSection.courtSystemId)
    const terms = await firstValueFrom(this.vocabService.observeTerms(originalHearingSection.courtSystemId))

    // Validate the update request
    const parseResult = parseStartEndTimes<HearingAnnotationTimeValidationReason>(action.hearingSection)
    if ('parseError' in parseResult) {
      this.handleHearingSectionErrors(parseResult.parseError, stateContext, action, terms)
      return
    }
    const { startTime, endTime } = parseResult

    // Prepare the updated hearing section
    const updatedHearingSection: HearingSectionModel = {
      ...originalHearingSection,
      startTime,
      endTime,
      caseReference: action.hearingSection.caseReference.trim() || undefined,
      title: action.hearingSection.title?.trim() || undefined,
      source: action.hearingSection.source,
      caseTypeId: action.hearingSection.caseTypeId,
      hearingTypeId: action.hearingSection.hearingTypeId,
    }

    // short-circuit if the updated hearing section is the same as the existing one
    if (
      originalHearingSection.startTime.equals(updatedHearingSection.startTime) &&
      originalHearingSection.endTime.equals(updatedHearingSection.endTime) &&
      originalHearingSection.caseReference === updatedHearingSection.caseReference &&
      originalHearingSection.title === updatedHearingSection.title &&
      originalHearingSection.caseTypeId === updatedHearingSection.caseTypeId &&
      originalHearingSection.hearingTypeId === updatedHearingSection.hearingTypeId
    ) {
      return await lastValueFrom(
        stateContext.dispatch(new ResetHearingSectionCommand(action.playbackKey, action.hearingSectionId)),
      )
    }

    // Validate the hearing section times
    const timeValidationError = validateHearingSectionTimes(
      startTime,
      endTime,
      playbackAnnotations.annotationBoundaries,
      playbackAnnotations.liveLocalTime,
    )
    if (timeValidationError) {
      this.handleHearingSectionErrors(timeValidationError, stateContext, action, terms)
      return
    }

    // Mark the hearing section as being in the process of updating
    stateContext.setState(
      patchHearingSection(annotationId, action.hearingSectionId, {
        status: 'saving',
        source: action.hearingSection.source,
      }),
    )

    const hasCaseReferenceChanged = updatedHearingSection.caseReference !== originalHearingSection.caseReference
    if (hasCaseReferenceChanged) {
      const caseTitleSuggestion = await this.getCaseTitleSuggestion(updatedHearingSection)
      updatedHearingSection.title = caseTitleSuggestion || updatedHearingSection.title
    }

    let updateResult
    let acknowledgedConflicts: HearingSectionConflict[] = []
    let previousConflictResolutionToken: string | undefined = undefined

    do {
      // get latest every attempt in case another user created new hearings that we have no info locally
      const fetchCommand = new FetchHearingSectionsCommand(action.playbackKey, originalHearingSection.courtSystemId)
      await this.fetchHearingSectionsCommand(stateContext, fetchCommand)
      const updatedPlaybackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)

      try {
        updateResult = await client.hearingSection.updateHearingSection({
          query: { conflictResolutionToken: previousConflictResolutionToken },
          body: {
            id: updatedHearingSection.id,
            startTime: updatedHearingSection.startTime,
            endTime: updatedHearingSection.endTime,
            title: updatedHearingSection.title ?? null,
            caseReference: updatedHearingSection.caseReference ?? null,
            recordingDate: action.recordingDate,
            locationId: action.locationId,
            isAutoGenerated: false,
            courtroomSessionId: action.courtroomSessionId,
            hearingTypeId: updatedHearingSection.hearingTypeId ?? null,
          },
        })
      } catch (error) {
        this.logger.error({ message: 'Failed to update hearing section', error })
        stateContext.setState(
          patchHearingSection(annotationId, action.hearingSectionId, {
            status: { errorMessage: 'Failed to save.', showRetry: true, showReset: false },
            source: action.hearingSection.source,
          }),
        )
        stateContext.dispatch(new HearingSectionUpdateFailedEvent(action, 'Failed to save.', false, true))
        return
      }
      if (updateResult.status === 200) {
        // Do the knock-on update to the case details if needed
        const updatedCase = await this.updateHearingSectionCaseDetails({
          originalHearingSection,
          updatedHearingSection,
        })

        const changes = {
          ...updatedHearingSection,
          isAutoGenerated: false,
          status: 'saved' as const,
          showAsOngoing: isOngoing(updatedPlaybackAnnotations.liveLocalTime, updatedHearingSection),
        }

        stateContext.setState(
          patch({
            playbackAnnotations: patch({
              [annotationId]: patch({
                hearingSections: patch({ [action.hearingSectionId]: patch(changes) }),
                // the command also updates case details
                // so we propagate those changes to the cases state
                cases: updatedCase ? patch({ [updatedCase.id]: patch({ ...updatedCase }) }) : undefined,
              }),
            }),
          }),
        )
        stateContext.dispatch(new HearingSectionUpdateSucceededEvent(action))

        // For each complete overlap that we have acknowledged we have caused a deletion
        acknowledgedConflicts
          .filter(conflict => conflict.type === 'complete-overlap')
          .forEach(conflict => {
            const hearingSection = updatedPlaybackAnnotations.hearingSections[conflict.hearingSectionId]
            if (!hearingSection) {
              return
            }
            stateContext.dispatch(
              new HearingSectionDeletedEvent(
                action.playbackKey,
                action.hearingSectionId,
                hearingSection.courtSystemId,
                'conflict',
                hearingSection.isAutoGenerated,
              ),
            )
          })
        stateContext.dispatch(
          new HearingSectionUpdatedEvent(action.playbackKey, originalHearingSection.id, originalHearingSection, {
            ...updatedHearingSection,
            isAutoGenerated: false,
          }),
        )
      } else if (updateResult.status === 409 && updateResult.body.type === 'HearingSectionConflicts') {
        const hasConflictResolutionTokenExpired =
          previousConflictResolutionToken !== undefined &&
          previousConflictResolutionToken !== updateResult.body.conflictResolutionToken
        previousConflictResolutionToken = updateResult.body.conflictResolutionToken

        const shouldAcknowledgeConflicts = await this.shouldAcknowledgeConflicts(
          updateResult.body.conflicts,
          this.getOrInitializePlaybackAnnotations(stateContext, action),
          hasConflictResolutionTokenExpired,
          originalHearingSection.courtSystemId,
        )
        if (!shouldAcknowledgeConflicts) {
          stateContext.setState(
            patchHearingSection(annotationId, updatedHearingSection.id, {
              forceResetFormKey: generateUuid(),
              status: 'loaded',
              source: action.hearingSection.source,
            }),
          )
          return
        }
        acknowledgedConflicts = updateResult.body.conflicts
      } else {
        this.logger.error({ message: 'Unexpected status code when updating hearing section', response: updateResult })
        const errorMessage =
          updateResult.body && 'error' in updateResult.body ? updateResult.body.error : 'Failed to save.'
        stateContext.setState(
          patchHearingSection(annotationId, action.hearingSectionId, {
            status: { errorMessage, showRetry: true, showReset: false },
            source: action.hearingSection.source,
          }),
        )
      }
    } while (updateResult.status === 409 && updateResult.body.type === 'HearingSectionConflicts')
  }

  private async updateHearingSectionCaseDetails(action: {
    originalHearingSection: HearingSectionModel
    updatedHearingSection: HearingSectionModel
  }): Promise<CaseDisplay | undefined> {
    const { originalHearingSection, updatedHearingSection } = action
    const client = await this.getApiClient(originalHearingSection.courtSystemId)

    if (
      updatedHearingSection.caseId &&
      updatedHearingSection.caseReference &&
      updatedHearingSection.caseTypeId !== originalHearingSection.caseTypeId
    ) {
      try {
        const updatedCase = await client.case.updateCase({
          params: {
            caseId: updatedHearingSection.caseId,
          },
          body: {
            caseReference: updatedHearingSection.caseReference ?? null,
            title: updatedHearingSection.title ?? null,
            caseTypeId: updatedHearingSection.caseTypeId ?? null,
          },
        })

        if (updatedCase.status === 200) {
          return updatedCase.body
        }
      } catch (error) {
        this.logger.error({ message: 'Failed to update case type', error })
      }
    }
    return undefined
  }

  @Action(AcceptAutoGeneratedHearingSectionCommand)
  async acceptAutoGeneratedHearingSectionCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: AcceptAutoGeneratedHearingSectionCommand,
  ): Promise<void> {
    const annotationId = getPlaybackId(action.playbackKey)
    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)
    const originalHearingSection = playbackAnnotations.hearingSections[action.hearingSectionId]
    if (!originalHearingSection) {
      return
    }
    const client = await this.getApiClient(originalHearingSection.courtSystemId)

    stateContext.setState(
      patchHearingSection(annotationId, action.hearingSectionId, {
        status: 'saving',
        source: RealTimeSttMarkerEditSource.Header,
      }),
    )

    let updateResult
    try {
      updateResult = await client.hearingSection.acceptAutoGeneratedHearingSection({
        query: {
          hearingSectionId: action.hearingSectionId,
        },
      })
    } catch (error) {
      this.logger.error({ message: 'Failed to accept automatically generated hearing section', error })
    }
    if (updateResult?.status === 200) {
      stateContext.setState(
        patchHearingSection(annotationId, action.hearingSectionId, {
          status: 'saved',
          isAutoGenerated: false,
        }),
      )

      stateContext.dispatch(new HearingSectionAcceptedEvent(originalHearingSection))
    } else {
      stateContext.setState(
        patchHearingSection(annotationId, action.hearingSectionId, {
          status: { errorMessage: 'Failed to save.', showRetry: false, showReset: false },
          source: RealTimeSttMarkerEditSource.Header,
        }),
      )
      if (!updateResult) {
        this.logger.error({
          message: 'Unexpected status code when accepting a automatically generated hearing section',
          response: updateResult,
        })
      }
    }
  }

  @Action(UpdateSessionAnnotationsCommand)
  async updateSessionAnnotationsCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: UpdateSessionAnnotationsCommand,
  ): Promise<void> {
    const {
      playbackKey: { recordingId, type },
      event: { judgeSessionAppearances, courtStaffSessionAppearances, courtroomSessionId },
      courtSystemId,
    } = action
    if (type !== 'recording') {
      return
    }
    const client = await this.getApiClient(courtSystemId)
    const hasCourtStaffFeatureFlag = await firstValueFrom(
      this.launchDarklyService.observeFlagChanges('feature-annotations-court-staff'),
    )

    try {
      const judgeVersionIds = compact(
        judgeSessionAppearances?.map(judgeSessionAppearance => judgeSessionAppearance.profile.profileVersionId),
      )
      const courtStaffVersionIds = hasCourtStaffFeatureFlag
        ? compact(
            courtStaffSessionAppearances?.map(
              courtStaffSessionAppearance => courtStaffSessionAppearance.profile.profileVersionId,
            ),
          )
        : undefined

      await Promise.all([
        judgeSessionAppearances
          ? client.sessionAppearances.updateSessionAppearances({
              body: {
                courtSystemId,
                recordingId,
                courtroomSessionId,
                peopleVersionIds: judgeVersionIds,
                involvement: 'judge',
              },
            })
          : Promise.resolve({ status: 200, body: undefined }),
        courtStaffSessionAppearances && courtStaffVersionIds
          ? client.sessionAppearances.updateSessionAppearances({
              body: {
                courtSystemId,
                recordingId,
                courtroomSessionId,
                peopleVersionIds: courtStaffVersionIds,
                involvement: 'court_staff',
              },
            })
          : Promise.resolve({ status: 200, body: undefined }),
      ]).then(updateResults => {
        if (updateResults.some(result => result.status !== 200)) {
          this.logger.error({
            message: 'Failed to update session annotations',
            responses: updateResults
              .filter(result => result.status !== 200)
              .map(result => result.body ?? 'Unknown error'),
          })
          return stateContext.dispatch(
            new SessionAnnotationsUpdateFailedEvent(action, 'Failed to update session annotations'),
          )
        }

        return stateContext.dispatch(new SessionAnnotationsUpdateSucceededEvent(action))
      })
    } catch (error) {
      this.logger.error({ message: 'Failed to update session annotations', error })
      stateContext.dispatch(new SessionAnnotationsUpdateFailedEvent(action, 'Failed to update session annotations'))
    }
  }

  private async getCaseTitleSuggestion({
    courtSystemId,
    recordingId,
    caseReference,
  }: HearingSectionModel): Promise<string> {
    if (!caseReference?.trim()) {
      return ''
    }

    const client = await this.getApiClient(courtSystemId)
    try {
      const suggestionResponse = await client.hearingSection.getSuggestionsForHearingSection({
        query: {
          courtSystemId,
          recordingId,
          caseReference,
        },
      })
      if (suggestionResponse.status === 200 && suggestionResponse.body.caseTitleSuggestion) {
        return suggestionResponse.body.caseTitleSuggestion
      } else {
        this.logger.error({ message: 'Failed to get case title suggestion', response: suggestionResponse })
      }
    } catch (error) {
      this.logger.error({ message: 'Unexpected status code when getting case title suggestion', error })
    }
    return ''
  }

  private handleHearingSectionErrors(
    validationResult: HearingAnnotationTimeValidation,
    { setState, dispatch }: StateContext<AnnotationsStateModel>,
    action: UpdateHearingSectionCommand,
    terms: VocabularyTerms,
  ): void {
    const errorMessage = mapHearingTimeValidationReasonMessage(validationResult, terms)
    setState(
      patchHearingSection(getPlaybackId(action.playbackKey), action.hearingSectionId, {
        status: {
          errorMessage,
          fields: mapValidationReasonToFields(validationResult.reason),
          showRetry: false,
          showReset: true,
        },
        source: action.hearingSection.source,
      }),
    )
    dispatch(new HearingSectionUpdateFailedEvent(action, errorMessage, true, false))
  }

  @Action(DeleteHearingSectionCommand)
  async deleteHearingSectionCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    { playbackKey, id, courtSystemId, source }: DeleteHearingSectionCommand,
  ): Promise<void> {
    const terms = await firstValueFrom(this.vocabService.observeTerms(courtSystemId))
    const originalState = stateContext.getState()
    const annotationId = getPlaybackId(playbackKey)
    const client = await this.getApiClient(courtSystemId)
    const hearingSectionId = id
    const hearingSection = originalState.playbackAnnotations[annotationId].hearingSections[hearingSectionId]
    if (!hearingSection) {
      return
    }

    if (source === RealTimeSttMarkerEditSource.Header) {
      stateContext.setState(
        patchHearingSection(annotationId, id, {
          status: 'saving',
          source: RealTimeSttMarkerEditSource.Header,
        }),
      )
    }

    let deleteResult
    try {
      deleteResult = await client.hearingSection.deleteHearingSection({
        query: { hearingSectionId },
      })
    } catch (error) {
      this.logger.error({ message: 'Failed to delete hearing section', error })
    }
    if (deleteResult?.status === 204) {
      const newHearingSections = { ...originalState.playbackAnnotations[annotationId].hearingSections }
      delete newHearingSections[hearingSectionId]
      const caseId = hearingSection.caseId
      const isCaseOnAnotherHearing = Object.values(newHearingSections).some(
        hs => hs.caseId && caseId && hs.caseId === caseId,
      )
      const newCases = { ...originalState.playbackAnnotations[annotationId].cases }
      if (!isCaseOnAnotherHearing && caseId) {
        delete newCases[caseId]
      }

      stateContext.setState(
        patch({
          playbackAnnotations: patch({
            [annotationId]: patch({
              hearingSections: newHearingSections,
              cases: newCases,
            }),
          }),
        }),
      )
      stateContext.dispatch(
        new HearingSectionDeletedEvent(
          playbackKey,
          hearingSectionId,
          courtSystemId,
          source === RealTimeSttMarkerEditSource.Header ? 'delete auto-annotation' : 'popover',
          hearingSection.isAutoGenerated,
        ),
      )
    } else {
      if (source === RealTimeSttMarkerEditSource.Header) {
        stateContext.setState(
          patchHearingSection(annotationId, id, {
            status: {
              errorMessage: 'Failed to delete',
              showRetry: false,
              showReset: false,
            },
            source: RealTimeSttMarkerEditSource.Header,
          }),
        )
      } else {
        this.toastService.error(`Failed to delete ${terms.hearing.singular}.`)
      }
      if (deleteResult) {
        this.logger.warn({ message: 'Unexpected status code when deleting hearing section', response: deleteResult })
      }
    }
  }

  @Action(UpdateHearingSectionSummaryVisibilityCommand)
  async updateHearingSectionSummaryVisibilityCommand(
    { setState }: StateContext<AnnotationsStateModel>,
    action: UpdateHearingSectionSummaryVisibilityCommand,
  ): Promise<void> {
    const clientV2 = await this.getV2ApiClient(action.courtSystemId)

    const response = await clientV2.hearingSections.summaries.update({
      params: { courtSystemId: action.courtSystemId, hearingSectionId: action.id },
      body: { isHidden: action.isHidden },
    })

    if (response.status === 200) {
      return setState(
        patchHearingSection(getPlaybackId(action.playbackKey), action.id, {
          summary: response.body,
        }),
      )
    } else {
      this.toastService.error('Failed to update summary')
    }
  }

  @Action(GenerateHearingSummaryCommand)
  async generateHearingSummaryCommand(
    { setState }: StateContext<AnnotationsStateModel>,
    action: GenerateHearingSummaryCommand,
  ): Promise<void> {
    if (action.mode === 'persistent') {
      this.regionalApiClientFactory
        .buildForCourt('/hearing-section', action.courtSystemId)
        .pipe(
          switchMap(apiClient =>
            apiClient.post<HearingSummaryResponse>(
              {
                path: `${action.id}/request-summary?immediate=true&skipComparison=true`,

                headers: { courtSystemId: action.courtSystemId },
              },
              { rawError: true },
            ),
          ),
          tapData(() => {
            this.toastService.success('Summary requested')
          }),
          tapFailure(() => {
            this.toastService.error('Failed to request summary')
          }),
        )
        .subscribe()
    }

    if (action.mode === 'ephemeral') {
      const generatingToast = this.toastService.info('Generating ephemeral summary...', undefined, {
        disableTimeOut: true,
      })
      this.regionalApiClientFactory
        .buildForCourt('/hearing-section', action.courtSystemId)
        .pipe(
          switchMap(apiClient =>
            apiClient.post<HearingSummaryResponse>(
              {
                path: `${action.id}/generate-ephemeral-summary`,
                headers: { courtSystemId: action.courtSystemId },
                responseBodyParser: HearingSummaryResponseSchema,
              },
              { rawError: true },
            ),
          ),
          tapData(result => {
            this.toastService.clear(generatingToast.toastId)
            this.toastService.success('Ephemeral summary generated')
            console.log('Summary generated.', { ...result, summary: { ...result.summary, summary: '[redacted]' } })

            setState(
              patchHearingSection(getPlaybackId(action.playbackKey), action.id, {
                summary: {
                  id: generateUuid(),
                  courtSystemId: action.courtSystemId,
                  hearingSectionId: action.id,
                  text: result.summary.summary,
                  isHidden: false,
                  generationId: result.summary.generationId,
                  generatedAt: result.summary.generatedAt,
                  isEphemeral: true,
                },
              }),
            )
          }),
          tapFailure(() => {
            this.toastService.clear(generatingToast.toastId)
            this.toastService.error('Failed to generate ephemeral summary')
          }),
        )
        .subscribe()
    }
  }

  @Action(ListenForHearingUpdatesCommand)
  async listenForHearingUpdatesCommand(
    { dispatch }: StateContext<AnnotationsStateModel>,
    action: ListenForHearingUpdatesCommand,
  ): Promise<void> {
    const auth = await this.authTokenFetcher.getToken()
    const region = await firstValueFrom(
      this.courtSystemRegionCache.getOrFetchCourtSystem(of(action.courtSystemId)),
    ).then(courtSystem => courtSystem.region)

    const joinHearingAnnotationsRoomRequest = buildJoinHearingAnnotationsRoomRequest(
      action.playbackKey,
      action.courtSystemId,
      region,
      auth,
    )
    if (!joinHearingAnnotationsRoomRequest) {
      return
    }

    this.realTimeLiveHearingsService
      .observeLiveHearingUpdates(joinHearingAnnotationsRoomRequest)
      .pipe(
        throttleTime(500),
        takeUntil(
          this.actions.pipe(
            ofActionDispatched(StopListeningForHearingSectionUpdatesCommand),
            filter(x => isEqual(x.playbackKey, action.playbackKey)),
          ),
        ),
      )
      .subscribe(() => {
        // on hearing update events, refresh the cases
        // because we don't know what has changed by other users
        // TODO: user doesn't need to subscribe to its own updates, only to other browsers
        dispatch(new FetchHearingSectionsCommand(action.playbackKey, action.courtSystemId, true))
        if (action.playbackKey.recordingId) {
          dispatch(
            new FetchPeopleAnnotationsCommand(action.courtSystemId, action.playbackKey.recordingId, { force: true }),
          )
        }
      })

    const autoAnnoJobUpdates$ = this.realTimeLiveHearingsService.observeAutomaticAnnotationJobUpdates(
      joinHearingAnnotationsRoomRequest,
    )
    merge(
      // take everything until the first AutomaticAnnotationsHearingIdentificationStarted event
      autoAnnoJobUpdates$.pipe(
        takeWhile(x => x.$name !== 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationStarted'),
      ),
      // but then after the start event, we explicitly wait for its corresponding finished event, and ensure that the
      // loading state is always displayed for a minimum time for readability
      autoAnnoJobUpdates$.pipe(
        filter(x => x.$name === 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationStarted'),
        switchMap(started =>
          combineLatest([
            autoAnnoJobUpdates$.pipe(
              filter(
                update =>
                  update.$name !== 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationStarted' &&
                  started.requestId === update.requestId,
              ),
            ),
            timer(AUTO_ANNOS_MIN_LOADING_TOAST_DISPLAY_MS),
          ]).pipe(
            map(([finished]) => finished),
            startWith(started),
          ),
        ),
      ),
    )
      .pipe(
        takeUntil(
          this.actions.pipe(
            ofActionDispatched(StopListeningForHearingSectionUpdatesCommand),
            filter(x => isEqual(x.playbackKey, action.playbackKey)),
          ),
        ),
      )
      .subscribe(update => {
        dispatch(new UpdateAutomaticAnnotationJobAction(action.playbackKey, action.courtSystemId, update))
      })
  }

  @Action(UpdateAnnotationBoundariesCommand)
  updateAnnotationBoundariesCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: UpdateAnnotationBoundariesCommand,
  ): void {
    const annotationId = getPlaybackId(action.playbackKey)
    this.getOrInitializePlaybackAnnotations(stateContext, action)
    stateContext.setState(
      patch({
        playbackAnnotations: patch({
          [annotationId]: patch({
            annotationBoundaries: action.annotationBoundaries,
          }),
        }),
      }),
    )
  }

  @Action(UpdateAnnotationsLiveLocalTimeCommand)
  updateAnnotationsLiveLocalTimeCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: UpdateAnnotationsLiveLocalTimeCommand,
  ): void {
    const annotationId = getPlaybackId(action.playbackKey)
    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)
    stateContext.setState(
      patch({
        playbackAnnotations: patch({
          [annotationId]: patch({
            liveLocalTime: action.liveLocalTime,
            hearingSections: Object.fromEntries(
              Object.entries(playbackAnnotations.hearingSections).map(([id, hearingSection]) => [
                id,
                {
                  ...hearingSection,
                  showAsOngoing: isOngoing(action.liveLocalTime, hearingSection),
                },
              ]),
            ),
          }),
        }),
      }),
    )
  }

  @Action(StopOngoingHearingForRecordingCommand)
  stopOngoingHearingForRecordingCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    action: StopOngoingHearingForRecordingCommand,
  ): Observable<void> {
    // find the ongoing hearing section since there can only be one
    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, action)
    const ongoingHearingSection = Object.values(playbackAnnotations.hearingSections).find(x => x.showAsOngoing)
    const liveLocalTime = playbackAnnotations.liveLocalTime?.withNano(0)

    if (!ongoingHearingSection || !liveLocalTime) {
      return of()
    }

    return stateContext.dispatch(
      new UpdateHearingSectionCommand(
        action.playbackKey,
        ongoingHearingSection.id,
        {
          ...ongoingHearingSection,
          caseReference: ongoingHearingSection.caseReference ?? '',
          title: ongoingHearingSection.title ?? '',
          startTime: ongoingHearingSection.startTime.toString(),
          endTime: liveLocalTime.toString(),
        },
        action.recordingDate,
        action.locationId,
        action.courtroomSessionId,
        'stop-ongoing',
      ),
    )
  }

  @Action(UpdateAutomaticAnnotationJobAction)
  async updateAutomaticAnnotationJobAction(
    stateContext: StateContext<AnnotationsStateModel>,
    { update, playbackKey, courtSystemId }: UpdateAutomaticAnnotationJobAction,
  ): Promise<void> {
    const annotationId = getPlaybackId(playbackKey)
    const playbackAnnotations = this.getOrInitializePlaybackAnnotations(stateContext, { playbackKey })
    const { latestAutomaticAnnotationJob } = playbackAnnotations
    // Skip updates if for different request ids as long as it's not the start of a job (as this indicates its more recent)
    const skipUpdate =
      latestAutomaticAnnotationJob &&
      latestAutomaticAnnotationJob.requestId !== update.requestId &&
      update.$name !== 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationStarted'
    if (skipUpdate) {
      return
    }

    if (latestAutomaticAnnotationJob) {
      this.toastService.clear(latestAutomaticAnnotationJob.toastId)
    }

    const terms = await firstValueFrom(this.vocabService.observeTerms(courtSystemId))
    const toast = showToastForAutomaticAnnotationJobUpdate(this.toastService, terms, update)

    stateContext.setState(
      patch({
        playbackAnnotations: patch({
          [annotationId]: patch({
            latestAutomaticAnnotationJob: {
              requestId: update.requestId,
              toastId: toast.toastId,
            },
          }),
        }),
      }),
    )
  }

  @Action(ResetHearingSectionCommand)
  resetHearingSectionCommand(
    stateContext: StateContext<AnnotationsStateModel>,
    { playbackKey, hearingSectionId }: ResetHearingSectionCommand,
  ): void {
    stateContext.setState(
      patchHearingSection(getPlaybackId(playbackKey), hearingSectionId, {
        status: 'loaded',
        forceResetFormKey: generateUuid(),
      }),
    )
  }

  private async getApiClient(courtSystemId: Uuid): Promise<AnnotationsApiClient> {
    const { region } = await firstValueFrom(this.courtSystemRegionCache.getOrFetchCourtSystem(of(courtSystemId)))
    return this.annotationsApiClientFactory.build(region)
  }

  private async getV2ApiClient(courtSystemId: Uuid): Promise<AnnotationsApiClientV2> {
    const { region } = await firstValueFrom(this.courtSystemRegionCache.getOrFetchCourtSystem(of(courtSystemId)))
    return this.annotationsApiClientFactoryV2.build(region)
  }

  private getOrInitializePlaybackAnnotations(
    { getState, setState }: StateContext<AnnotationsStateModel>,
    { playbackKey }: { playbackKey: RealTimePlaybackKey },
  ): RecordingAnnotationsModel {
    const originalState = getState()
    const annotationId = getPlaybackId(playbackKey)
    let playbackAnnotations = originalState.playbackAnnotations[annotationId]
    if (!playbackAnnotations) {
      playbackAnnotations = defaultPlaybackAnnotationsState()
      setState(
        patch({
          playbackAnnotations: patch({
            [annotationId]: playbackAnnotations,
          }),
        }),
      )
    }
    return playbackAnnotations
  }

  private async shouldAcknowledgeConflicts(
    conflicts: HearingSectionConflict[],
    playbackAnnotations: RecordingAnnotationsModel,
    hasConflictResolutionTokenExpired: boolean,
    courtSystemId: Uuid,
  ): Promise<boolean> {
    const terms = await firstValueFrom(this.vocabService.observeTerms(courtSystemId))
    const completeOverlaps = compact(
      conflicts
        .filter(conflict => conflict.type === 'complete-overlap')
        .map(conflict => playbackAnnotations.hearingSections[conflict.hearingSectionId]),
    )

    if (completeOverlaps.length === 0) {
      // we don't bother prompting the user if there are no complete overlaps (deletions)
      return true
    }

    const hearingTerm = completeOverlaps.length > 1 ? terms.hearing.plural : terms.hearing.singular
    return await this.confirmationModalService.confirm({
      modalType: ModalType.Error,
      title: `${titleCase(hearingTerm)} Conflict`,
      confirmText: `Delete ${hearingTerm}`,
      confirmButtonColor: 'danger',
      content: {
        type: HearingAnnotationConflictsModalComponent,
        inputs: {
          terms,
          hearingSections: completeOverlaps,
          hasPreviousConfirmationExpired: hasConflictResolutionTokenExpired,
        },
      },
    })
  }
}

function isOngoing(
  liveLocalTime: LocalTime | undefined,
  hearingSection: Pick<HearingSectionModel, 'startTime' | 'endTime'>,
): boolean {
  return liveLocalTime
    ? new LocalTimeRange(hearingSection.startTime, hearingSection.endTime).contains(liveLocalTime)
    : false
}

function mapValidationReasonToFields(
  validationResult: HearingAnnotationTimeValidationReason,
): (keyof HearingSectionModel)[] {
  const affectsAll = ['both-out-of-bounds', 'different-sessions', 'start-must-be-before-end', 'both-are-not-times']
  const startIsInvalid = ['start-is-out-of-bounds', 'start-is-not-a-time', ...affectsAll].includes(validationResult)
  const endIsInvalid = ['end-is-out-of-bounds', 'future-end-time', 'end-is-not-a-time', ...affectsAll].includes(
    validationResult,
  )
  const fields: (keyof HearingSectionModel)[] = []
  if (startIsInvalid) {
    fields.push('startTime')
  }
  if (endIsInvalid) {
    fields.push('endTime')
  }
  return fields
}

function patchHearingSection(
  annotationId: Uuid,
  hearingSectionId: Uuid,
  changes: Partial<HearingSectionModel>,
): StateOperator<AnnotationsStateModel> {
  return patch({
    playbackAnnotations: patch({
      [annotationId]: patch({
        hearingSections: patch({
          [hearingSectionId]: patch(changes),
        }),
      }),
    }),
  })
}

function showToastForAutomaticAnnotationJobUpdate(
  toastService: ToastService,
  terms: VocabularyTerms,
  update: AutomaticAnnotationJobUpdate,
): ActiveToast<ToastComponent> {
  switch (update.$name) {
    case 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationStarted':
      return toastService.info(`Looking for ${terms.hearing.plural}. Please wait...`, 'Info', {
        // Ensure we can replace loading without animating out as it results in the toasts temporally stacking
        easeTime: 0,
      })
    case 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationSkipped':
      return showToastForAutomaticAnnotationSkipped(toastService, terms, update)
    case 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationCompleted':
      return update.resultCount
        ? toastService.success(
            `${update.resultCount} ${update.resultCount > 1 ? `${terms.hearing.plural} were` : `${terms.hearing.singular} was`} identified in the last session.`,
          )
        : toastService.info(`No ${terms.hearing.plural} were identified in the last session.`)

    case 'ftr-regional-api/AutomaticAnnotationsHearingIdentificationFailed':
      return toastService.error(
        `${capitalize(terms.hearing.singular)} information could not be identified in the last session due to an unknown error.`,
      )
    default:
      assertUnreachable(update)
  }
}

function showToastForAutomaticAnnotationSkipped(
  toastService: ToastService,
  terms: VocabularyTerms,
  update: AutomaticAnnotationsHearingIdentificationSkipped,
): ActiveToast<ToastComponent> {
  switch (update.reason) {
    case AutomaticAnnotationsHearingIdentificationSkippedReason.UnsupportedRegion:
      return toastService.info(
        `Generating ${terms.hearing.singular} information automatically is not currently supported in this region.`,
      )
    case AutomaticAnnotationsHearingIdentificationSkippedReason.NoStt:
      return toastService.info(
        `Generating ${terms.hearing.singular} information automatically was skipped because no speech-to-text was found in the session.`,
      )
    case AutomaticAnnotationsHearingIdentificationSkippedReason.ExistingAnnotations:
      return toastService.info(
        `Generating ${terms.hearing.singular} information automatically was skipped because a ${terms.hearing.singular} was found in the session.`,
      )
    case AutomaticAnnotationsHearingIdentificationSkippedReason.ContainsSealedContent:
      return toastService.info(
        `Generating ${terms.hearing.singular} information automatically was skipped because ${terms.sealed.singular} content was found in the session.`,
      )
    default:
      assertUnreachable(update.reason)
  }
}
